import React from "react";
import { BrowserRouter, Navigate, Route, Routes, useNavigate } from "react-router-dom";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { Toaster } from "@/components/ui/sonner";

import { AuthProvider, useAuth } from "@/lib/auth.jsx";
import { AuthGuard } from "@/components/AuthGuard";
import { Layout } from "@/components/Layout";

import LoginPage from "@/pages/LoginPage";
import AuthCallbackPage from "@/pages/AuthCallbackPage";
import WorkflowLibraryPage from "@/pages/WorkflowLibraryPage";
import WorkflowEditorPage from "@/pages/WorkflowEditorPage";
import WorkflowDetailPage from "@/pages/WorkflowDetailPage";
import DemoRunsListPage from "@/pages/DemoRunsListPage";
import DemoRunDetailPage from "@/pages/DemoRunDetailPage";
import DemoHistoryPage from "@/pages/DemoHistoryPage";
import UsersPage from "@/pages/UsersPage";
import SharesAdminPage from "@/pages/SharesAdminPage";
import AdminEmailDeliveryPage from "@/pages/AdminEmailDeliveryPage";
import AdminSecurityStatusPage from "@/pages/AdminSecurityStatusPage";
import ShareDetailPage from "@/pages/ShareDetailPage";
import PublicSharePage from "@/pages/PublicSharePage";
import ViewerPlaceholderPage from "@/pages/ViewerPlaceholderPage";
import FoldersListPage from "@/pages/FoldersListPage";
import FolderDetailPage from "@/pages/FolderDetailPage";
import HelpPage from "@/pages/HelpPage";

import "@/App.css";

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      refetchOnWindowFocus: false,
      retry: false,
    },
  },
});

function RootRedirect() {
  const { user, loading } = useAuth();
  if (loading) {
    return (
      <div className="grid h-screen place-items-center text-slate-400" data-testid="root-loading">
        Loading…
      </div>
    );
  }
  return <Navigate to={user ? "/workflows" : "/login"} replace />;
}

function AppShell() {
  const navigate = useNavigate();
  // R3-Phase-1 — anonymous routes that should NOT redirect to /login
  // when the AuthProvider's initial `/api/auth/me` returns 401.  The
  // `/share/*` deprecation page is intentionally readable without an
  // account, and the brief explicitly mandates it does not bounce
  // anonymous visitors through the login screen.
  return (
    <AuthProvider
      onUnauthorized={() => {
        const path = window.location.pathname;
        const isAnon =
          path === "/login" ||
          path.startsWith("/share/") ||
          path.startsWith("/v/") ||
          path === "/auth/callback";
        if (isAnon) return;
        navigate("/login", { replace: true });
      }}
    >
      <Routes>
        <Route path="/" element={<RootRedirect />} />
        <Route path="/login" element={<LoginPage />} />
        <Route path="/auth/callback" element={<AuthCallbackPage />} />
        <Route path="/share/:token" element={<PublicSharePage />} />
        <Route path="/v/:token" element={<ViewerPlaceholderPage />} />
        <Route
          element={
            <AuthGuard>
              <Layout />
            </AuthGuard>
          }
        >
          <Route path="/workflows" element={<WorkflowLibraryPage />} />
          <Route path="/workflows/new" element={<WorkflowEditorPage mode="create" />} />
          <Route path="/workflows/:id" element={<WorkflowDetailPage />} />
          <Route path="/workflows/:id/edit" element={<WorkflowEditorPage mode="edit" />} />
          <Route path="/runs" element={<DemoRunsListPage />} />
          <Route path="/runs/:run_id" element={<DemoRunDetailPage />} />
          <Route path="/history" element={<DemoHistoryPage />} />
          <Route path="/folders" element={<FoldersListPage />} />
          <Route path="/folders/:folder_id" element={<FolderDetailPage />} />
          <Route path="/shares" element={<SharesAdminPage />} />
          <Route path="/shares/:invitation_id" element={<ShareDetailPage />} />
          <Route path="/users" element={<UsersPage />} />
          <Route path="/admin/email-delivery" element={<AdminEmailDeliveryPage />} />
          <Route path="/admin/security-status" element={<AdminSecurityStatusPage />} />
          <Route path="/help" element={<HelpPage />} />
          <Route path="/help/:slug" element={<HelpPage />} />
        </Route>
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </AuthProvider>
  );
}

function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <BrowserRouter>
        <AppShell />
        <Toaster richColors position="top-right" />
      </BrowserRouter>
    </QueryClientProvider>
  );
}

export default App;
